import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccessRole } from '@hector/shared-types/resource-sharing';
import type { CompactContextCapability, SessionStatus } from '../types/session.types';
import type { ConversationRouteOwner } from './conversationRouteOwnership';

export type ComposerAccessState = 'loading' | 'editable' | 'read_only' | 'unavailable';

/** Access authority paired with the URL conversation that produced it. */
export interface ConversationComposerAccessSnapshot {
  conversationId: string | null;
  accessState: ComposerAccessState;
}

interface MatchingLiveSessionKeyInput {
  sessionKey: string | null;
  targetConversationId: string | null;
  getSessionStatus: (sessionKey: string) => SessionStatus | null;
  getConversationId: (sessionKey: string) => string | null;
}

/**
 * Returns a reusable pool key only when its live slot still owns the target.
 *
 * A key is not a session identity by itself: route transitions can leave the
 * previous conversation's key in React state until the parent effect detaches
 * it. Every read or mutation must therefore validate the `(key, conversation)`
 * pair against the pool at the point of use.
 */
export function resolveMatchingLiveSessionKey({
  sessionKey,
  targetConversationId,
  getSessionStatus,
  getConversationId,
}: MatchingLiveSessionKeyInput): string | null {
  if (!sessionKey || !targetConversationId) return null;
  if (getSessionStatus(sessionKey) === null) return null;
  if (getConversationId(sessionKey) !== targetConversationId) return null;

  return sessionKey;
}

/** Route ownership after a send target finishes opening its WebSocket session. */
export type ConversationRouteActivation =
  | 'target_active'
  | 'new_target_owns_empty_route'
  | 'background';

interface ConversationComposerAccessInput {
  conversationId: string | null;
  isPendingConversation: boolean;
  /**
   * This client sent the conversation's first message, so it is the owner.
   *
   * Survives the phantom, which is dropped only after the global HTTP list sees
   * the DB row — the REST detail can still answer later on a weak connection.
   */
  isAuthoredInSession: boolean;
  detailRole: AccessRole | undefined;
  /** Global-list metadata is display-only and must not grant composer access. */
  fallbackRole: AccessRole | undefined;
  hasDetailError: boolean;
}

/**
 * Keeps the active composer deterministic while the URL detail is loading.
 *
 * The global list is display-only: every persisted conversation remains loading
 * until its authoritative detail resolves, regardless of a cached list role.
 *
 * Authorship is the one exception, and it is not a cache: minting the id and
 * having the backend accept the first message IS the proof of ownership. Waiting
 * on `GET /conversations/:id` to re-learn it locked the lawyer out of their own
 * thread mid-answer (2026-08-09) — and that request is itself gated on the
 * cabinet and user queries, so a slow network compounds the wait.
 */
export function resolveConversationComposerAccess(input: ConversationComposerAccessInput): ComposerAccessState {
  const {
    conversationId,
    isPendingConversation,
    isAuthoredInSession,
    detailRole,
    hasDetailError,
  } = input;

  if (!conversationId || isPendingConversation) return 'editable';
  // A downgrade cannot apply to a thread this client just created: no one can
  // revoke the author between the first message and the detail response.
  if (isAuthoredInSession && detailRole === undefined) return 'editable';
  // TanStack Query retains authoritative detail data when a background refetch
  // fails. Only an initial failure without that cached role blocks the composer.
  if (hasDetailError && detailRole === undefined) return 'unavailable';

  if (detailRole === 'viewer') return 'read_only';
  if (detailRole === 'owner' || detailRole === 'editor') return 'editable';

  return 'loading';
}

/** Prevents a stale UI callback from starting or retrying a conversation send. */
export function canUseConversationComposerActions(accessState: ComposerAccessState): boolean {
  return accessState === 'editable';
}

/**
 * Offrir « Compacter la conversation » dans le rond du composer.
 *
 * Le cas central est la conversation ROUVERTE : historique chargé, aucune socket
 * (elle ne s'ouvre qu'au premier envoi), donc capacité `unknown`. Le bouton est
 * alors offert et le clic tranche. Seul un refus explicite d'une socket le
 * retire. Rien à compacter dans un fil vide ou pas encore persisté, et un lecteur
 * ne peut pas agir sur le fil.
 */
export function canOfferConversationCompaction({
  accessState,
  conversationId,
  hasHistory,
  capability,
}: {
  accessState: ComposerAccessState;
  conversationId: string | null;
  hasHistory: boolean;
  capability: CompactContextCapability;
}): boolean {
  return canUseConversationComposerActions(accessState)
    && conversationId !== null
    && hasHistory
    && capability !== 'unsupported';
}

/**
 * Bloque l'envoi du composer tant que la conversation affichée a du travail en
 * cours (tour, envoi en ouverture, compaction manuelle).
 *
 * - Fil piloté par l'URL (`/assistant`) : la navigation change
 *   `window.location` de façon synchrone, et un composer persistant peut encore
 *   recevoir, pendant un rendu, un Entrée avec les props de A. Le travail de A ne
 *   doit pas bloquer la route vide B : ce message appartient à une nouvelle
 *   conversation, routée ensuite par le dernier callback.
 * - Fil piloté par l'hôte (dock dossier, assistant de numérotation) : l'identité
 *   vit dans l'état React de l'hôte et n'est JAMAIS écrite dans `?c=`. Comparer à
 *   l'URL rendait le blocage inopérant (rendu `X` contre URL `null`) : Entrée
 *   vidait le champ pendant un streaming ou une compaction alors que l'envoi était
 *   refusé, et le texte de l'avocat était perdu. Les props suivent l'hôte dans le
 *   même rendu : le travail en cours fait foi.
 */
export function shouldBlockComposerSubmission({
  conversationRouteOwner,
  renderedConversationId,
  browserConversationId,
  hasCurrentConversationWork,
}: {
  conversationRouteOwner: ConversationRouteOwner;
  renderedConversationId: string | null;
  browserConversationId: string | null;
  hasCurrentConversationWork: boolean;
}): boolean {
  if (!hasCurrentConversationWork) return false;
  if (conversationRouteOwner === 'host') return true;
  return renderedConversationId === browserConversationId;
}

/**
 * Un envoi est-il en vol pour ce fil ? Refuse alors une compaction manuelle.
 *
 * Pourquoi le verrou d'envoi en plus du streaming et du tour actif : pendant
 * l'ouverture de la socket ou le téléversement d'un premier envoi, le slot du
 * pool existe déjà sans `pendingMessage`. Le pool accepterait la compaction sur
 * ce slot, puis refuserait l'envoi (« une compaction est en cours ») : le message
 * de l'avocat serait retiré de l'écran.
 */
export function hasConversationSendInFlight({
  sendLocks,
  conversationId,
  hasActiveTranscriptTurn,
  isSessionStreaming,
}: {
  sendLocks: ConversationSendLocks;
  conversationId: string;
  hasActiveTranscriptTurn: boolean;
  isSessionStreaming: boolean;
}): boolean {
  return getConversationSendState(sendLocks, conversationId) !== null
    || hasActiveTranscriptTurn
    || isSessionStreaming;
}

/** Transient first-send work, displayed only for its target conversation. */
export interface ConversationSendState {
  targetConversationId: string;
  isOpening: boolean;
  uploadingFilenames: string[];
}

/**
 * Send locks retained only while opening a session or uploading.
 *
 * The chat component persists across URL navigation, so this cannot be a single
 * boolean: an awaited opening for A must not make B look busy or suppress B's send.
 * A blank route has no target id until send begins, so its lock identity is the
 * route instance while its display state remains attached to the generated target.
 */
export type ConversationSendLocks = Record<string, ConversationSendState>;

interface ConversationSendLockAcquireResult {
  locks: ConversationSendLocks;
  acquired: boolean;
}

export function acquireConversationSendLock(
  locks: ConversationSendLocks,
  lockIdentity: string,
  targetConversationId: string,
): ConversationSendLockAcquireResult {
  if (locks[lockIdentity] !== undefined) {
    return { locks, acquired: false };
  }

  return {
    locks: {
      ...locks,
      [lockIdentity]: { targetConversationId, isOpening: true, uploadingFilenames: [] },
    },
    acquired: true,
  };
}

/**
 * Gives a first send one stable lock before it awaits session creation.
 *
 * Existing conversations use their target id. A blank route must instead use
 * React Router's exact `location.key`: two synchronous clicks mint different
 * target UUIDs but still belong to the same composer instance.
 */
export function getConversationSendLockIdentity({
  targetConversationId,
  isNewConversation,
  locationKey,
}: {
  targetConversationId: string;
  isNewConversation: boolean;
  locationKey: string;
}): string {
  return isNewConversation
    ? `blank-route:${locationKey}`
    : `conversation:${targetConversationId}`;
}

/** Returns only the transient work belonging to the conversation being rendered. */
export function getConversationSendState(
  locks: ConversationSendLocks,
  conversationId: string | null,
): ConversationSendState | null {
  if (!conversationId) return null;
  return Object.values(locks).find(
    (state) => state.targetConversationId === conversationId,
  ) ?? null;
}

export function setConversationSendUploadingFiles(
  locks: ConversationSendLocks,
  lockIdentity: string,
  uploadingFilenames: string[],
): ConversationSendLocks {
  const existing = locks[lockIdentity];
  if (!existing) return locks;

  return {
    ...locks,
    [lockIdentity]: { ...existing, uploadingFilenames },
  };
}

/** Releases one lock so an older async completion cannot unlock a newer route. */
export function releaseConversationSendLock(
  locks: ConversationSendLocks,
  lockIdentity: string,
): ConversationSendLocks {
  if (locks[lockIdentity] === undefined) return locks;

  const { [lockIdentity]: _released, ...remainingLocks } = locks;
  return remainingLocks;
}

interface ConversationRouteActivationInput {
  targetConversationId: string;
  activeRouteConversationId: string | null;
  isNewConversation: boolean;
  newTargetOwnsEmptyRoute: boolean;
}

/**
 * Prevents an awaited session open from rebinding a route the user left.
 *
 * A target may activate the displayed session when the URL still names that
 * target. A frontend phantom may additionally claim an empty route once; once
 * another id is active, its session must stay registered in the background.
 */
export function resolveConversationRouteActivation({
  targetConversationId,
  activeRouteConversationId,
  isNewConversation,
  newTargetOwnsEmptyRoute,
}: ConversationRouteActivationInput): ConversationRouteActivation {
  if (activeRouteConversationId === targetConversationId) return 'target_active';
  if (isNewConversation && newTargetOwnsEmptyRoute && activeRouteConversationId === null) {
    return 'new_target_owns_empty_route';
  }

  return 'background';
}

interface PendingConversationRouteOwnership {
  pendingConversationId: string | null;
  beginPendingConversation: (conversationId: string) => void;
  clearPendingConversation: () => void;
  resolveRouteActivation: (
    targetConversationId: string,
    isNewConversation: boolean,
  ) => ConversationRouteActivation;
}

/** Retained display state for a first send started from the empty route. */
export function usePendingConversationRouteOwnership(
  activeRouteConversationId: string | null,
  activeRouteKey: string,
): PendingConversationRouteOwnership {
  const activeRouteRef = useRef({
    conversationId: activeRouteConversationId,
    key: activeRouteKey,
  });
  activeRouteRef.current = {
    conversationId: activeRouteConversationId,
    key: activeRouteKey,
  };
  const mountedRef = useRef(true);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);
  const pendingRouteKeyRef = useRef<string | null>(null);
  const emptyRouteCapabilityRef = useRef<{
    conversationId: string;
    routeKey: string;
  } | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Async session opens retain this callback past unmount. Revoking first
      // makes them background-only and prevents updates to an abandoned tree.
      mountedRef.current = false;
      emptyRouteCapabilityRef.current = null;
      pendingRouteKeyRef.current = null;
    };
  }, []);

  useEffect(() => {
    const pendingRouteKey = pendingRouteKeyRef.current;
    if (pendingRouteKey === null || pendingRouteKey === activeRouteKey) return;

    // location.key distinguishes a newer `/assistant` entry even when `?c=`
    // stays null, so a pending send cannot claim a route it did not start on.
    emptyRouteCapabilityRef.current = null;
    pendingRouteKeyRef.current = null;
    setPendingConversationId(null);
  }, [activeRouteKey]);

  const beginPendingConversation = useCallback((conversationId: string) => {
    if (!mountedRef.current) return;

    const activeRoute = activeRouteRef.current;
    pendingRouteKeyRef.current = activeRoute.key;
    emptyRouteCapabilityRef.current = activeRoute.conversationId === null
      ? { conversationId, routeKey: activeRoute.key }
      : null;
    setPendingConversationId(conversationId);
  }, []);
  const clearPendingConversation = useCallback(() => {
    emptyRouteCapabilityRef.current = null;
    pendingRouteKeyRef.current = null;
    if (!mountedRef.current) return;
    setPendingConversationId(null);
  }, []);
  const resolveRouteActivation = useCallback((
    targetConversationId: string,
    isNewConversation: boolean,
  ) => {
    if (!mountedRef.current) return 'background';

    const activeRoute = activeRouteRef.current;
    const emptyRouteCapability = emptyRouteCapabilityRef.current;
    const activation = resolveConversationRouteActivation({
      targetConversationId,
      activeRouteConversationId: activeRoute.conversationId,
      isNewConversation,
      newTargetOwnsEmptyRoute: emptyRouteCapability?.conversationId === targetConversationId
        && emptyRouteCapability.routeKey === activeRoute.key,
    });

    if (activation === 'new_target_owns_empty_route') {
      // The blank-route claim is a capability, not durable ownership. A retry
      // or reopened session must not spend it a second time. Clear the display
      // id at the same boundary: `handleConversationCreated` replaces the URL,
      // so a later "new conversation" can keep the same history key. Retaining
      // A here would make that new blank route inherit A's stream/transcript.
      emptyRouteCapabilityRef.current = null;
      pendingRouteKeyRef.current = null;
      setPendingConversationId((currentId) => (
        currentId === targetConversationId ? null : currentId
      ));
    }

    if (activation === 'background') {
      // A background open remains registered, but it must immediately stop
      // supplying display state to whatever route the lawyer visits next.
      emptyRouteCapabilityRef.current = null;
      pendingRouteKeyRef.current = null;
      setPendingConversationId((currentId) => (
        currentId === targetConversationId ? null : currentId
      ));
    }

    return activation;
  }, []);

  // React Router publishes the next location during render, whereas the effect
  // above clears the stale state only afterwards. Never let a first-send claim
  // from route A make an editor/session from A look active on a newer blank
  // route B in that render gap.
  const pendingConversationForActiveRoute = pendingRouteKeyRef.current === activeRouteKey
    && emptyRouteCapabilityRef.current?.conversationId === pendingConversationId
    ? pendingConversationId
    : null;

  return {
    pendingConversationId: pendingConversationForActiveRoute,
    beginPendingConversation,
    clearPendingConversation,
    resolveRouteActivation,
  };
}

interface ConversationTargetSendAccessInput {
  targetConversationId: string;
  isNewConversation: boolean;
  capturedSnapshot: ConversationComposerAccessSnapshot;
  currentSnapshot: ConversationComposerAccessSnapshot;
}

/**
 * Resolves the final send permission against the conversation being mutated.
 *
 * The current route remains authoritative only while it still identifies the
 * target, so a same-conversation downgrade is caught. After navigation, the
 * captured target state is the only relevant fallback: the new route's role
 * must never approve or reject a pending send for another conversation.
 */
export function canSendToConversationTarget({
  targetConversationId,
  isNewConversation,
  capturedSnapshot,
  currentSnapshot,
}: ConversationTargetSendAccessInput): boolean {
  if (isNewConversation) return true;

  const targetSnapshot = currentSnapshot.conversationId === targetConversationId
    ? currentSnapshot
    : capturedSnapshot.conversationId === targetConversationId
      ? capturedSnapshot
      : undefined;

  return targetSnapshot !== undefined
    && canUseConversationComposerActions(targetSnapshot.accessState);
}
